//@ts-nocheck

import { useState, useEffect } from "react";
import { Button } from "@/components/Button/Button";
import Link from "next/link";
import { useRouter } from "next/router";

const Default = ({ content, data = { table: {} } }) => {
  const { header = "Header", subheader = "Subheader" } = content;
  const router = useRouter();
  const { column } = router.query;

  const [vehicleType, setVehicleType] = useState(() => {
    const tables = data?.table || {};
    return Object.keys(tables)[0] || "";
  });
  const [highlightedColumn, setHighlightedColumn] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Update highlighted column when url changes
  useEffect(() => {
    if (!router.isReady) return;

    if (column) {
      const models = data?.table?.[vehicleType];
      if (models) {
        const availableModels = Object.keys(models).filter(
          (key) => models[key]?.data
        );
        const columnIndex = availableModels.indexOf(column);
        if (columnIndex !== -1) {
          setHighlightedColumn(columnIndex);
        }
      }
    }
  }, [router.isReady, column, data, vehicleType]);

  const handleMouseEnter = (index) => {
    setHoveredColumn(index);
  };

  const handleMouseLeave = () => {
    setHoveredColumn(null);
  };

  // Early return if no data
  if (!data?.table || Object.keys(data.table).length === 0) {
    return (
      <div className="container mx-auto py-12 text-center text-gray-600">
        Keine Daten verfügbar
      </div>
    );
  }

  const renderTable = (models) => {
    if (!models) return null;

    // Get all unique headers from all models
    const headers = new Set();
    Object.values(models).forEach((model) => {
      if (model?.data) {
        Object.keys(model.data).forEach((key) => headers.add(key));
      }
    });

    const headerArray = Array.from(headers);
    const availableModels = Object.keys(models).filter(
      (key) => models[key]?.data
    );

    // Get the first model's link data
    const firstModel = availableModels[0];
    const linkData = models[firstModel]?.link;

    return (
      <div className="flex flex-col gap-8">
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="table-fixed min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="bg-blue-dark text-white p-6 text-left font-medium border-r border-white">
                  Spezifikation
                </th>
                {availableModels.map((model, colIndex) => (
                  <th
                    key={model}
                    onMouseEnter={() => handleMouseEnter(colIndex)}
                    onMouseLeave={handleMouseLeave}
                    className={`bg-blue-dark text-white p-6 text-center font-medium border-r border-white last:border-r-0 ${
                      highlightedColumn === colIndex
                        ? "bg-blue-light"
                        : hoveredColumn === colIndex
                        ? "bg-blue-light/50"
                        : ""
                    }`}
                  >
                    {model}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {headerArray.map((header, rowIndex) => {
                const isTypeHeader = availableModels.some(
                  (model) => models[model]?.data[header] === "type_header"
                );

                if (isTypeHeader) {
                  return (
                    <tr key={rowIndex} className="bg-blue-medium text-white">
                      <td
                        colSpan={availableModels.length + 1}
                        className="text-left font-bold p-6 whitespace-normal"
                      >
                        {header.replace(/_/g, " ")}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={header}
                    className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="border-2 border-white font-bold bg-gray-100 p-6 !max-w-[250px]">
                      {header.replace(/_/g, " ")}
                    </td>
                    {availableModels.map((model, colIndex) => (
                      <td
                        key={`${model}-${header}`}
                        onMouseEnter={() => handleMouseEnter(colIndex)}
                        onMouseLeave={handleMouseLeave}
                        className={`border-2 border-gray-100 px-10 text-center text-gray-600 py-2 whitespace-nowrap ${
                          highlightedColumn === colIndex
                            ? "bg-[#008CBA29]"
                            : hoveredColumn === colIndex
                            ? "bg-[#008CBA29]/50"
                            : ""
                        }`}
                      >
                        {models[model]?.data[header] || "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {linkData && (
          <div className="flex w-full justify-center">
            <Link
              href={linkData.href}
              target={linkData.target}
              className="w-full"
            >
              <Button type="primary" className="w-full !min-w-screen">
                {linkData.linkText} Angebote
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto">
      <div className="flex flex-col items-center justify-center py-12">
        <h1 className="lg:text-7xl text-5xl font-bold text-blue-dark mb-2">
          {header}
        </h1>
        <h2 className="lg:text-5xl text-3xl font-normal text-blue-dark text-center max-w-2xl">
          {subheader}
        </h2>
      </div>

      <div className="mb-8">
        {Object.entries(data.table).map(([type, models]) => (
          <div key={type}>{renderTable(models)}</div>
        ))}
      </div>
    </div>
  );
};

export default Default;
